import { Component, EventEmitter, inject, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AccessLevelTileComponent } from '../../../tiles/access-level-tile/access-level-tile.component';
import { ReplaceParamPipe } from '../../../../pipes/replace-param/replace-param.pipe';
import {
    resetAddUserStepperUserSelection,
    updateAddUserStepperSelectedAccess,
    updateAddUserStepperSelectedAccount,
} from '../../../../store/actions/add-user-stepper-flow.action';
import { Store } from '@ngrx/store';
import { ActivatedRoute } from '@angular/router';
import { AdobeLaunchService } from '../../../../services/adobe-launch/adobe-launch.service';
import { AccessTypeSelection } from '../../../../../common/enums/access-type-selection.enum';
import { environment } from 'src/environments/environment';
import { IneligibleReasons } from '../../../../../common/enums/ineligible-scenarios.enum';
import { ModalDialogComponent } from '@vg-constellation/angular-20/modal-dialog';
import { ContentUtil } from '../../../../utils/content/content.util';
import { AdobeTagging } from '../../../../utils/decorators/dev/adobe-launch';
import { adobeConfigData } from './gaf-access-level-mvp-adobe';
import { selectAddUserStepperSelectedAccount } from '../../../../store/selectors/grant-revoke-permissions.selector';
import {
    selectAccountById,
    selectedAccountIsIneligibleFor,
} from '../../../../store/selectors/account-permissions.selector';
import { take, tap } from 'rxjs';
import { OwnAccountDTO } from '../../../../../common/dtos/own-account.dto';
import { ACCOUNT_ID_PARAM } from '../../../../constants/application-routes';
import { LinkTarget } from '../../../../enums/link-target.enum';
import { AccessLevelData } from '../../../../interfaces/access-level-with-label.interface';

@AdobeTagging(adobeConfigData)
@Component({
    selector: 'zci-gaf-access-level',
    standalone: true,
    imports: [CommonModule, AccessLevelTileComponent, ModalDialogComponent, ReplaceParamPipe],
    templateUrl: './gaf-access-level-mvp.component.html',
})
export class GafAccessLevelMvpComponent implements OnInit {
    @Input() nextStepReady: EventEmitter<any>;
    @ViewChild('selectAccessModalDialogue', { static: false })
    selectAccessModalDialogue!: ModalDialogComponent;
    @ViewChild('cashPlusModalDialogue', { static: false })
    cashPlusModalDialogue!: ModalDialogComponent;
    accountId: string;
    environment = environment;
    grantorContent = ContentUtil.grantorFragmentContent;
    ineligibleReasons: IneligibleReasons[] = [];
    headingText: string = '';
    bodyText: string = '';
    cashManagementAccount: boolean;
    selectedAccess: AccessTypeSelection;
    // Transformed AEM content for tiles
    infoOnlyTileData: AccessLevelData;
    limitedTileData: AccessLevelData;
    fullAuthorityTileData: AccessLevelData;
    protected adobeLaunchService = inject(AdobeLaunchService);
    private readonly store = inject(Store);
    private readonly activatedRoute = inject(ActivatedRoute);

    ngOnInit(): void {
        this.accountId = this.activatedRoute.snapshot.params[ACCOUNT_ID_PARAM];
        this.storeSelectedAccount();

        // Reset user selection when entering access level step
        this.store.dispatch(resetAddUserStepperUserSelection());

        this.store.select(selectedAccountIsIneligibleFor(this.accountId)).subscribe((data) => {
            this.ineligibleReasons = data;
        });

        this.store.select(selectAddUserStepperSelectedAccount).subscribe((data) => {
            this.cashManagementAccount = data?.cashManagementAccount;
        });

        // Initialize tile data from AEM content
        this.initializeTileData();
    }

    openCashPlusModalDialog() {
        this.cashPlusModalDialogue.openModalDialog();
    }

    selectInfoOnlyAndNavigate(): void {
        this.selectedAccess = AccessTypeSelection.INFORMATION_ONLY;
        if (this.cashManagementAccount === true) {
            this.openCashPlusModalDialog();
        } else {
            this.store.dispatch(
                updateAddUserStepperSelectedAccess({
                    selectedAccess: this.selectedAccess,
                }),
            );
            this.nextStepReady.emit(true);
        }
    }

    selectLimitedAndNavigate(): void {
        this.selectedAccess = AccessTypeSelection.LIMITED_ACCESS;
        if (this.ineligibleReasons.includes(IneligibleReasons.MANAGED_ACCOUNT)) {
            this.selectAccessModalDialogue.openModalDialog();
        } else {
            this.store.dispatch(
                updateAddUserStepperSelectedAccess({
                    selectedAccess: this.selectedAccess,
                }),
            );
            this.nextStepReady.emit(true);
        }
    }

    navigateToForm(): void {
        window.open(environment.AGENT_AUTHORIZATION_FORM_URL, LinkTarget.Self);
    }

    onContinueModal(): void {
        this.store.dispatch(
            updateAddUserStepperSelectedAccess({
                selectedAccess: this.selectedAccess,
            }),
        );
        this.nextStepReady.emit(true);
    }

    private initializeTileData(): void {
        if (this.grantorContent?.add_user?.frmcSelectAccessLevel) {
            const accessLevelContent = this.grantorContent.add_user.frmcSelectAccessLevel;

            // Transform Information Only tile data
            if (accessLevelContent.frmfInformationOnly) {
                this.infoOnlyTileData = {
                    accessLevel: accessLevelContent.frmfInformationOnly.labelText,
                    completion: accessLevelContent.frmfInformationOnly.lblOnlineCompletion,
                    info: this.parseHtmlToArray(accessLevelContent.frmfInformationOnly.body),
                };
            }

            // Transform Limited Authority tile data
            if (accessLevelContent.frmfLimitedAccess) {
                this.limitedTileData = {
                    accessLevel: accessLevelContent.frmfLimitedAccess.labelText,
                    completion: accessLevelContent.frmfLimitedAccess.lblOnlineCompletion,
                    info: this.parseHtmlToArray(accessLevelContent.frmfLimitedAccess.body),
                };
            }

            // Transform Full Authority tile data
            if (accessLevelContent.frmfFullAuthority) {
                this.fullAuthorityTileData = {
                    accessLevel: accessLevelContent.frmfFullAuthority.labelText,
                    completion: accessLevelContent.frmfFullAuthority.lblFormCompletion,
                    notaryRequired: accessLevelContent.frmfFullAuthority.lblNotaryRequired,
                    info: this.parseHtmlToArray(accessLevelContent.frmfFullAuthority.body),
                };
            }
        }
    }

    private parseHtmlToArray(htmlString: string): string[] {
        if (!htmlString) return [];

        // Parse HTML string to extract list items
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');
        const listItems = doc.querySelectorAll('li');

        return Array.from(listItems)
            .map((li) => li.textContent?.trim() || '')
            .filter((text) => text.length > 0);
    }

    private storeSelectedAccount() {
        this.store
            .select(selectAccountById(this.accountId))
            .pipe(
                take(1),
                tap((selectedAccount: OwnAccountDTO) => {
                    this.store.dispatch(
                        updateAddUserStepperSelectedAccount({
                            selectedAccount,
                        }),
                    );
                }),
            )
            .subscribe();
    }
}
